import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Badge,
  Modal,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import {
  FaEnvelope,
  FaUserShield,
  FaEdit,
  FaSave,
  FaCheckCircle,
  FaInfoCircle,
  FaUpload,
  FaDownload,
  FaUserTie,
  FaClipboardCheck,
  FaFileDownload
} from "react-icons/fa";
import '../../ComponentsCss/DPOManagement.css';
import { useAuth } from "../../contexts/AuthContext";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

// â”€â”€â”€ Theme Tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SUSAN = {
  primary: '#01696f',
  primaryHover: '#0c4e54',
  primaryLight: '#e6f4f4',
  primaryMid: '#cedcd8',
};
const T = {
  brand: '#0095C4',
  brandLight: 'rgba(0, 149, 196, 0.10)',
  success: '#33A148',
  error: '#DC3545',
  white: '#FFFFFF',
  bodyBg: '#F3F5F8',
  cardBg: '#FFFFFF',
  cardShadow: '0 2px 12px rgba(0,0,0,0.08)',
  textPrimary: '#1A2332',
  textSecondary: '#5A6577',
  textMuted: '#8E99A8',
  border: '#E2E6EC',
  borderLight: '#F0F2F5',
  radiusSm: '6px',
  radiusMd: '10px',
  radiusLg: '14px',
  font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

// â”€â”€â”€ Regulation Config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DPO_REG_CONFIG = {
  dpdp: {
    title: 'DPO Management â€” Significant Data Fiduciary',
    subtitle: 'DPDP Act 2023 â€” Section 10(2)',
    flag: 'ðŸ‡®ðŸ‡³',
    legalBasis: 'DPDP Act 2023, Section 10',
    appointmentRule: 'Mandatory for all Significant Data Fiduciaries (SDFs)',
    locationReq: 'Must be based in India',
    reportsTo: 'Board of Directors or equivalent governing body',
    primaryRole: 'Point of contact for grievance redressal & DPBI compliance',
    keyDuties: [
      'Act as point of contact for Data Protection Board of India (DPBI)',
      'Manage grievance redressal mechanism for data principals',
      'Ensure compliance with DPDP Act provisions',
      'Coordinate with Board of Directors on data protection matters',
      'Oversee DPIA execution and periodic audits',
      'Monitor Significant Data Fiduciary obligations',
    ],
    dpiaLabel: 'DPIA (Data Protection Impact Assessment)',
    dpiaLegalRef: 'Section 10(2)(c) â€” Mandatory for SDFs',
    auditLabel: 'Periodic Audit',
    auditLegalRef: 'Section 10(2)(b) â€” Annual audit by independent auditor',
    emailTemplatePrefix: 'DPDP',
    regulatorName: 'Data Protection Board of India (DPBI)',
    penaltyWarning: 'Non-compliance with SDF obligations can result in penalties up to â‚¹250 Crore',
    showAuditorSection: true,
    showRegistrationNumber: true,
    registrationLabel: 'DPBI Registration Number',
    independenceLabel: 'Independence Declaration (DPDP Act)',
  },
  gdpr: {
    title: 'DPO Management â€” Data Protection Officer',
    subtitle: 'GDPR â€” Articles 37, 38, 39',
    flag: 'ðŸ‡ªðŸ‡º',
    legalBasis: 'GDPR Articles 37-39',
    appointmentRule: 'Mandatory for: public authorities, large-scale systematic monitoring, large-scale sensitive data processing',
    locationReq: 'No location requirement â€” can be based anywhere in the EU or externally',
    reportsTo: 'Highest management level (directly to board/executive)',
    primaryRole: 'Independent advisor, compliance monitor & supervisory authority liaison',
    keyDuties: [
      'Inform and advise controller/processor on GDPR obligations (Art 39.1a)',
      'Monitor compliance with GDPR and internal policies (Art 39.1b)',
      'Advise on and monitor Data Protection Impact Assessments (Art 39.1c)',
      'Cooperate with supervisory authority (Art 39.1d)',
      'Act as contact point for supervisory authority (Art 39.1e)',
      'Manage staff awareness training and processing audits',
    ],
    dpiaLabel: 'DPIA (Data Protection Impact Assessment)',
    dpiaLegalRef: 'Article 35 â€” Required for high-risk processing',
    auditLabel: 'Compliance Audit',
    auditLegalRef: 'Article 39.1(b) â€” DPO monitors compliance including audits',
    emailTemplatePrefix: 'GDPR',
    regulatorName: 'National Data Protection Authority (DPA)',
    penaltyWarning: 'DPO-related infringements under Articles 37-39 can result in fines up to â‚¬10M or 2% of global turnover',
    showAuditorSection: true,
    showRegistrationNumber: false,
    registrationLabel: '',
    independenceLabel: 'Independence & No Conflict of Interest Declaration (Art 38.6)',
  },
  pdpl: {
    title: 'DPO Management â€” Data Protection Officer',
    subtitle: 'UAE PDPL â€” Federal Decree-Law No. 45 of 2021, Article 10',
    flag: 'ðŸ‡¦ðŸ‡ª',
    legalBasis: 'UAE PDPL Article 10',
    appointmentRule: 'Required when processing poses high privacy risk (new technology, large-scale sensitive data, profiling)',
    locationReq: 'No specific location requirement â€” recommended within UAE',
    reportsTo: 'Controller management / Board level',
    primaryRole: 'Compliance officer, DPIA coordinator & UAE Data Office liaison',
    keyDuties: [
      'Monitor compliance with UAE PDPL and executive regulations',
      'Coordinate Data Protection Impact Assessments',
      'Act as liaison with the UAE Data Office',
      'Advise on cross-border data transfer safeguards',
      'Ensure Record of Processing Activities (RoPA) maintenance',
      'Oversee data breach notification procedures',
    ],
    dpiaLabel: 'DPIA (Data Protection Impact Assessment)',
    dpiaLegalRef: 'Article 10 â€” Required before high-risk processing',
    auditLabel: 'Compliance Review',
    auditLegalRef: 'UAE PDPL â€” Periodic compliance review recommended',
    emailTemplatePrefix: 'UAE PDPL',
    regulatorName: 'UAE Data Office',
    penaltyWarning: 'Non-compliance can result in penalties up to AED 5,000,000',
    showAuditorSection: true,
    showRegistrationNumber: false,
    registrationLabel: '',
    independenceLabel: 'Independence Declaration (UAE PDPL)',
  },
};

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DPOManagementTab = ({ userData, showNotification, regulation = 'dpdp' }) => {
  const [dpoInfo, setDpoInfo] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    registrationNumber: "",
    status: "active",
    appointmentDate: "",
    reportingTo: "",
    alternateDpo: "",
    independenceDeclared: false,
    lastIndependenceAffirmed: ""
  });

  const [auditorInfo, setAuditorInfo] = useState({
    auditorName: "",
    auditorEmail: "",
    auditorPhone: "",
    auditorFirm: "",
    appointmentDate: "",
    certificationNumber: "",
    nextAuditDate: ""
  });

  const [periodicMeasures, setPeriodicMeasures] = useState({
    lastDPIADate: "",
    nextDPIADate: "",
    dpiaStatus: "pending",
    lastAuditDate: "",
    nextAuditDate: "",
    uploadedDPIA: null
  });

  // âœ… OrgId from AuthContext
  const { profile } = useAuth();
  const organizationId = profile?.orgId;

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAuditor, setIsEditingAuditor] = useState(false);
  const [isEditingMeasures, setIsEditingMeasures] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [dpiaFile, setDpiaFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const getUserRegion = () => {
    return sessionStorage.getItem("userRegion") || "IN";
  };

  // âœ… useEffect now depends on organizationId
  useEffect(() => {
    if (organizationId) {
      fetchDPOInfo();
      fetchAuditorInfo();
      fetchPeriodicMeasures();
    }
  }, [organizationId]);


  // âœ… OrgId header in all fetch functions
  const fetchDPOInfo = async () => {
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://48hrxjdbve.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/info",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          }
        }
      );
      const data = await res.json();
      if (data.dpoInfo) {
        setDpoInfo(data.dpoInfo);
      }
    } catch (err) {
      console.error("Error fetching DPO info", err);
    }
  };

  const fetchAuditorInfo = async () => {
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://98wjuwtclf.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/auditor/info",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          }
        }
      );
      const data = await res.json();
      if (data.auditorInfo) {
        setAuditorInfo(data.auditorInfo);
      }
    } catch (err) {
      console.error("Error fetching auditor info", err);
    }
  };

  // const fetchPeriodicMeasures = async () => {
  //   const region = getUserRegion();
  //   try {
  //     const res = await fetch(
  //       "https://m3xumqxuz7.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/periodic-measures",
  //       {
  //         method: "GET",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Region: region,
  //           OrgId: organizationId   // âœ… Fixed
  //         }
  //       }
  //     );
  //     const data = await res.json();
  //     if (data.measures) {
  //       setPeriodicMeasures(data.measures);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching periodic measures", err);
  //   }
  // };

/****region data  */
  const fetchPeriodicMeasures = async () => {
    const region = getUserRegion();
    try {
        const res = await fetch(
            "https://m3xumqxuz7.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/periodic-measures",
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Region: region,
                    OrgId: organizationId
                }
            }
        );
        const data = await res.json();

        // âœ… Read from regionData map using current region
        if (data.regionData && data.regionData[region]) {
            setPeriodicMeasures(data.regionData[region]);
        } else if (data.measures) {
            // fallback
            setPeriodicMeasures(data.measures);
        }
    } catch (err) {
        console.error("Error fetching periodic measures", err);
    }
};

  const handleUpdateDPO = async (e) => {
    e.preventDefault();
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://tvcbq15zed.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          },
          body: JSON.stringify(dpoInfo)
        }
      );
      if (res.ok) {
        showNotification("DPO information updated successfully", "success");
        setIsEditing(false);
        fetchDPOInfo();
      }
    } catch (err) {
      console.error("Error updating DPO info", err);
      showNotification("Error updating DPO information", "danger");
    }
  };

  const handleUpdateAuditor = async (e) => {
    e.preventDefault();
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://lex0l93m58.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/auditor/update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          },
          body: JSON.stringify(auditorInfo)
        }
      );
      if (res.ok) {
        showNotification("Auditor information updated successfully", "success");
        setIsEditingAuditor(false);
        fetchAuditorInfo();
      }
    } catch (err) {
      console.error("Error updating auditor info", err);
      showNotification("Error updating auditor information", "danger");
    }
  };

  const handleUpdatePeriodicMeasures = async () => {
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://0llmj7kyl3.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/periodic-measures/update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          },
          body: JSON.stringify(periodicMeasures)
        }
      );
      if (res.ok) {
        showNotification("Periodic measures updated successfully", "success");
        setIsEditingMeasures(false);
        fetchPeriodicMeasures();
      }
    } catch (err) {
      console.error("Error updating periodic measures", err);
      showNotification("Error updating periodic measures", "danger");
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    const region = getUserRegion();
    try {
      const res = await fetch(
        "https://z72tn6ag41.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/send-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          },
          body: JSON.stringify({
            to: emailRecipient || dpoInfo.email,
            subject: emailSubject,
            body: emailBody,
            from: dpoInfo.email
          })
        }
      );
      if (res.ok) {
        showNotification("Email sent successfully", "success");
        setShowEmailModal(false);
        setEmailSubject("");
        setEmailBody("");
        setEmailRecipient("");
      }
    } catch (err) {
      console.error("Error sending email", err);
      showNotification("Error sending email", "danger");
    }
  };

  const handleDPIAUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const region = getUserRegion();
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const res = await fetch(
        "https://ynozqqmmgi.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/dpia/upload",
        {
          method: "POST",
          headers: {
            Region: region,
            OrgId: organizationId   // âœ… Fixed (no Content-Type here â€” FormData sets it automatically)
          },
          body: formData
        }
      );
      if (res.ok) {
        const data = await res.json();
        const updatedMeasures = {
          ...periodicMeasures,
          uploadedDPIA: data.fileKey,
          lastDPIADate: new Date().toISOString().split('T')[0],
          dpiaStatus: "completed"
        };
        setPeriodicMeasures(updatedMeasures);
        await savePeriodicMeasuresAfterUpload(updatedMeasures);
        showNotification("DPIA uploaded successfully", "success");
      }
    } catch (err) {
      console.error("Error uploading DPIA", err);
      showNotification("Error uploading DPIA", "danger");
    } finally {
      setUploading(false);
    }
  };

  const savePeriodicMeasuresAfterUpload = async (measures) => {
    const region = getUserRegion();
    try {
      await fetch(
        "https://0llmj7kyl3.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/periodic-measures/update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          },
          body: JSON.stringify(measures)
        }
      );
    } catch (err) {
      console.error("Error saving periodic measures after upload", err);
    }
  };

  const handleDownloadUploadedDPIA = async () => {
    if (!periodicMeasures.uploadedDPIA) {
      showNotification("No DPIA file uploaded yet", "warning");
      return;
    }
    const region = getUserRegion();
    try {
      const res = await fetch(
        `https://yijme07mw5.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpo/dpia/download?fileKey=${encodeURIComponent(periodicMeasures.uploadedDPIA)}`,
        {
          method: "GET",
          headers: {
            Region: region,
            OrgId: organizationId   // âœ… Fixed
          }
        }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `DPIA_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showNotification("DPIA downloaded successfully", "success");
      }
    } catch (err) {
      console.error("Error downloading DPIA", err);
      showNotification("Error downloading DPIA", "danger");
    }
  };

  // const handleDownloadDPIATemplate = () => {
  //   const link = document.createElement('a');
  //   link.href = '/templates/DPIA_Template.docx';
  //   link.download = 'DPIA_Template.docx';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  // const loadEmailTemplate = (type) => {
  //   const today = new Date().toLocaleDateString('en-GB');
  //   const templates = {
  //     grievance_acknowledgment: {
  //       subject: "Grievance Acknowledgment - DPDP Compliance",
  //       body: `Dear Data Principal,\n\nWe acknowledge receipt of your grievance submitted on ${today}.\nYour grievance has been assigned ID: [GRIEVANCE_ID].\n\nOur Data Protection Officer will review your complaint and respond within 30 days as per DPDP Act 2023.\n\nBest regards,\n${dpoInfo.name}\nData Protection Officer\n${dpoInfo.email}`
  //     },
  //     breach_notification: {
  //       subject: "Data Breach Notification - Urgent Action Required",
  //       body: `Dear Stakeholder,\n\nWe are writing to inform you of a data security incident that may affect your personal data.\n\n[BREACH DETAILS]\n\nImmediate actions taken:\n[ACTIONS]\n\nFor questions, please contact:\n${dpoInfo.name}\nData Protection Officer\n${dpoInfo.email}`
  //     },
  //     compliance_reminder: {
  //       subject: "DPDP Compliance Reminder",
  //       body: `Dear Team,\n\nThis is a reminder regarding upcoming DPDP compliance requirements:\n\n[COMPLIANCE ITEMS]\n\nPlease ensure timely completion.\n\nRegards,\n${dpoInfo.name}\nData Protection Officer\n${dpoInfo.email}`
  //     },
  //     audit_request: {
  //       subject: "Data Audit Request - DPDP Act 2023",
  //       body: `Dear ${auditorInfo.auditorName || 'Auditor'},\n\nAs per DPDP Act 2023 requirements, we request you to conduct a periodic data audit of our processing activities.\n\nAudit Scope:\n- Review of data processing activities\n- Compliance with DPDP Act provisions\n- Security measures assessment\n- DPIA validation\n\nPlease confirm your availability.\n\nBest regards,\n${dpoInfo.name}\nData Protection Officer\n${dpoInfo.email}`
  //     }
  //   };
  //   const template = templates[type];
  //   if (template) {
  //     setEmailSubject(template.subject);
  //     setEmailBody(template.body);
  //   }
  // };

  // const dpiaInfoTooltip = (
  //   <Tooltip id="dpia-info-tooltip">
  //     <strong>DPIA is Mandatory for SDF</strong>
  //     <br /><br />
  //     Section 10(2)(c) of the DPDP Act explicitly requires all SDFs to conduct
  //     DPIAs (Data Protection Impact Assessments) periodically to assess and
  //     mitigate risks associated with personal data processing.
  //   </Tooltip>
  // );

  // const handleDownloadDPIATemplate = () => {
  //   const link = document.createElement('a');
  //   link.href = '/templates/DPIA_Template.docx';
  //   link.download = 'DPIA_Template.docx';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const handleDownloadDPIATemplate = async () => {
    const regCfg = DPO_REG_CONFIG[regulation] || DPO_REG_CONFIG.dpdp;
    const doc = new Document({
        sections: [{
            children: [
                new Paragraph({ text: "DATA PROTECTION IMPACT ASSESSMENT (DPIA) TEMPLATE", heading: HeadingLevel.HEADING_1 }),
                new Paragraph({ text: `Under ${regCfg.legalBasis}`, heading: HeadingLevel.HEADING_2 }),
                new Paragraph({ text: "" }),
                new Paragraph({ children: [new TextRun({ text: "Organization Name: ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Project/Activity Name: ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Date of Assessment: ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Prepared by (DPO): ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 1: PROJECT/ACTIVITY OVERVIEW", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "1.1 Description of the processing activity:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "1.2 Purpose of processing personal data:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "1.3 Categories of personal data involved:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "1.4 Data Principals affected (estimated count):", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 2: NECESSITY AND PROPORTIONALITY", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "2.1 Is the processing necessary for the stated purpose?", bold: true })] }),
                new Paragraph({ text: "    [ ] Yes   [ ] No" }),
                new Paragraph({ text: "    Justification: _______________________________" }),
                new Paragraph({ children: [new TextRun({ text: `2.2 Legal basis for processing (${regCfg.legalBasis}):`, bold: true })] }),
                new Paragraph({ text: "    [ ] Consent   [ ] Legitimate Use   [ ] Other: ______" }),
                new Paragraph({ children: [new TextRun({ text: "2.3 Data minimization measures in place:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 3: RISK IDENTIFICATION", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "3.1 Risk 1:", bold: true }), new TextRun(" _____________________________________")] }),
                new Paragraph({ text: "    Likelihood: [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "    Impact:     [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "" }),
                new Paragraph({ children: [new TextRun({ text: "3.2 Risk 2:", bold: true }), new TextRun(" _____________________________________")] }),
                new Paragraph({ text: "    Likelihood: [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "    Impact:     [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "" }),
                new Paragraph({ children: [new TextRun({ text: "3.3 Risk 3:", bold: true }), new TextRun(" _____________________________________")] }),
                new Paragraph({ text: "    Likelihood: [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "    Impact:     [ ] High  [ ] Medium  [ ] Low" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 4: COMPLIANCE MEASURES", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "4.1 Technical security measures:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "4.2 Organizational security measures:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "4.3 Data retention policy:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ children: [new TextRun({ text: "4.4 Data Principal rights facilitation:", bold: true })] }),
                new Paragraph({ text: "    _____________________________________________" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 5: RISK MITIGATION", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "5.1 Mitigation for Risk 1:", bold: true })] }),
                new Paragraph({ text: "    Action: _____________________________________" }),
                new Paragraph({ text: "    Responsible Party: __________________________" }),
                new Paragraph({ text: "    Target Date: ________________________________" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 6: CONSULTATION", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ text: "6.1 Was the DPO consulted?  [ ] Yes   [ ] No" }),
                new Paragraph({ text: `6.2 Was the ${regCfg.regulatorName} consulted?` }),
                new Paragraph({ text: "    [ ] Yes   [ ] No   [ ] Not Required" }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "SECTION 7: SIGN-OFF", heading: HeadingLevel.HEADING_3 }),
                new Paragraph({ children: [new TextRun({ text: "DPO Name:      ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "DPO Signature: ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Date:          ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Reviewed by:   ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ children: [new TextRun({ text: "Approval Date: ", bold: true }), new TextRun("_______________________")] }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: `Prepared as per ${regCfg.legalBasis} â€” ${regCfg.dpiaLegalRef}` }),
            ],
        }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "DPIA_Template.docx");
};

  const loadEmailTemplate = (type) => {
    const regCfg = DPO_REG_CONFIG[regulation] || DPO_REG_CONFIG.dpdp;
    const today = new Date().toLocaleDateString('en-GB');
    
    const templates = {
      grievance_acknowledgment: {
        subject: "Grievance Acknowledgment - DPDP Compliance",
        body: `Dear Data Principal,

We acknowledge receipt of your grievance submitted on ${today}. 
Your grievance has been assigned ID: [GRIEVANCE_ID].

Our Data Protection Officer will review your complaint and respond within the statutory timeframe as per ${regCfg.legalBasis}.

Best regards,
${dpoInfo.name}
Data Protection Officer
${dpoInfo.email}`
      },
      breach_notification: {
        subject: "Data Breach Notification - Urgent Action Required",
        body: `Dear Stakeholder,

We are writing to inform you of a data security incident that may affect your personal data.

[BREACH DETAILS]

Immediate actions taken:
[ACTIONS]

For questions, please contact:
${dpoInfo.name}
Data Protection Officer
${dpoInfo.email}`
      },
      compliance_reminder: {
        subject: `${regCfg.emailTemplatePrefix} Compliance Reminder`,
        body: `Dear Team,

This is a reminder regarding upcoming ${regCfg.emailTemplatePrefix} compliance requirements:

[COMPLIANCE ITEMS]

Please ensure timely completion.

Regards,
${dpoInfo.name}
Data Protection Officer
${dpoInfo.email}`
      },
      audit_request: {
        subject: `Data Audit Request - ${regCfg.legalBasis}`,
        body: `Dear ${auditorInfo.auditorName || 'Auditor'},

As per ${regCfg.legalBasis} requirements, we request you to conduct a periodic data audit of our processing activities.

Audit Scope:
- Review of data processing activities
- Compliance with ${regCfg.emailTemplatePrefix} provisions
- Security measures assessment
- DPIA validation

Please confirm your availability.

Best regards,
${dpoInfo.name}
Data Protection Officer
${dpoInfo.email}`
      }
    };

    const template = templates[type];
    if (template) {
      setEmailSubject(template.subject);
      setEmailBody(template.body);
    }
  };

  // â”€â”€â”€ Derive regulation config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const regCfg = DPO_REG_CONFIG[regulation] || DPO_REG_CONFIG.dpdp;

  const dpiaInfoTooltip = (
    <Tooltip id="dpia-info-tooltip">
      <strong>{regCfg.dpiaLabel}</strong>
      <br /><br />
      {regCfg.dpiaLegalRef}
    </Tooltip>
  );


  return (
    <div className="dpo-management-tab">

      {/* â”€â”€ Regulation Info Banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Card style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, marginBottom: '20px', borderLeft: `4px solid ${SUSAN.primary}`, overflow: 'hidden' }}>
        <Card.Body style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <span style={{ fontSize: '1.5rem' }}>{regCfg.flag}</span>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: T.textPrimary }}>{regCfg.title}</div>
              <div style={{ fontSize: '12px', color: T.textMuted }}>{regCfg.subtitle}</div>
            </div>
          </div>

          {/* Legal basis + appointment rule */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ background: T.borderLight, borderRadius: T.radiusSm, padding: '10px 14px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Legal Basis</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: T.textPrimary }}>{regCfg.legalBasis}</div>
            </div>
            <div style={{ background: T.borderLight, borderRadius: T.radiusSm, padding: '10px 14px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Appointment Rule</div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: T.textPrimary }}>{regCfg.appointmentRule}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ background: T.borderLight, borderRadius: T.radiusSm, padding: '10px 14px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Location Requirement</div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: T.textPrimary }}>{regCfg.locationReq}</div>
            </div>
            <div style={{ background: T.borderLight, borderRadius: T.radiusSm, padding: '10px 14px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Reports To</div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: T.textPrimary }}>{regCfg.reportsTo}</div>
            </div>
          </div>

          {/* Key Duties */}
          <div style={{ background: SUSAN.primaryLight, borderRadius: T.radiusSm, padding: '12px 14px', border: `1px solid ${SUSAN.primaryMid}` }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: SUSAN.primary, marginBottom: '8px' }}>Key DPO Duties â€” {regCfg.legalBasis}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              {regCfg.keyDuties.map((duty, i) => (
                <div key={i} style={{ fontSize: '11px', color: T.textSecondary, display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: 1.4 }}>
                  <span style={{ color: SUSAN.primary, fontSize: '8px', marginTop: '4px', flexShrink: 0 }}>â—</span>
                  {duty}
                </div>
              ))}
            </div>
          </div>

          {/* Penalty warning */}
          <div style={{ marginTop: '10px', fontSize: '11px', color: T.error, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
            âš ï¸ {regCfg.penaltyWarning}
          </div>
        </Card.Body>
      </Card>

      {/* DPO Information Card */}
      <Card className="mb-4" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ background: T.white, borderBottom: '1px solid ' + T.border, padding: '16px 20px' }}>
          <h5 style={{ color: T.textPrimary, fontSize: '15px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaUserShield /> {regCfg.flag} Data Protection Officer Information
          </h5>
          <div>
            {isEditing ? (
              <Button variant="success" size="sm" onClick={handleUpdateDPO} style={{ background: T.success, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                <FaSave /> Save Changes
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setIsEditing(true)} style={{ background: SUSAN.primary, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                <FaEdit /> Edit
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>DPO Name</Form.Label>
                  <Form.Control type="text" value={dpoInfo.name} disabled={!isEditing}
                    onChange={(e) => setDpoInfo({ ...dpoInfo, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control type="email" value={dpoInfo.email} disabled={!isEditing}
                    onChange={(e) => setDpoInfo({ ...dpoInfo, email: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control type="tel" value={dpoInfo.phone} disabled={!isEditing}
                    onChange={(e) => setDpoInfo({ ...dpoInfo, phone: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Designation</Form.Label>
                  <Form.Control type="text" value={dpoInfo.designation} disabled={!isEditing}
                    onChange={(e) => setDpoInfo({ ...dpoInfo, designation: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {regCfg.showRegistrationNumber && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{regCfg.registrationLabel}</Form.Label>
                    <Form.Control type="text" value={dpoInfo.registrationNumber} disabled={!isEditing}
                      onChange={(e) => setDpoInfo({ ...dpoInfo, registrationNumber: e.target.value })} />
                  </Form.Group>
                </Col>
              )}
              <Col md={regCfg.showRegistrationNumber ? 6 : 12}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div className="dpo-status">
                    <Badge bg="success"><FaCheckCircle /> {dpoInfo.status.toUpperCase()}</Badge>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control type="date" value={dpoInfo.appointmentDate} disabled={!isEditing}
                    onChange={(e) => setDpoInfo({ ...dpoInfo, appointmentDate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Reports To</Form.Label>
                  <Form.Control type="text" value={dpoInfo.reportingTo} disabled={!isEditing}
                    placeholder="Board / CEO"
                    onChange={(e) => setDpoInfo({ ...dpoInfo, reportingTo: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <Alert variant="info" className="mt-3">
            The DPO serves as the primary point of contact for data principals and
            the {regCfg.regulatorName}. All contact details must be publicly
            accessible as per {regCfg.legalBasis}.
          </Alert>

          <Card className="mt-3" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
            <Card.Body>
              <Form.Check
                type="checkbox"
                label={`I declare that the DPO operates independently without conflict of interest â€” ${regCfg.independenceLabel}`}
                checked={dpoInfo.independenceDeclared}
                disabled={!isEditing}
                onChange={(e) =>
                  setDpoInfo({
                    ...dpoInfo,
                    independenceDeclared: e.target.checked,
                    lastIndependenceAffirmed: new Date().toISOString()
                  })
                }
              />
              {dpoInfo.lastIndependenceAffirmed && (
                <small className="text-muted">
                  Last affirmed on {new Date(dpoInfo.lastIndependenceAffirmed).toLocaleDateString()}
                </small>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Independent Data Auditor */}
      <Card className="mb-4" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ background: T.white, borderBottom: '1px solid ' + T.border, padding: '16px 20px' }}>
          <h5 style={{ color: T.textPrimary, fontSize: '15px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaUserTie /> Appoint an Independent Data Auditor
          </h5>
          <div>
            {isEditingAuditor ? (
              <Button variant="success" size="sm" onClick={handleUpdateAuditor} style={{ background: T.success, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                <FaSave /> Save Changes
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setIsEditingAuditor(true)} style={{ background: SUSAN.primary, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                <FaEdit /> Edit
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="mb-3">
            <strong>Purpose:</strong> Conducts data audits to evaluate compliance with {regCfg.legalBasis} â€” {regCfg.auditLegalRef}
          </Alert>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auditor Name</Form.Label>
                  <Form.Control type="text" value={auditorInfo.auditorName} disabled={!isEditingAuditor}
                    placeholder="Full Name"
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, auditorName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auditor Email</Form.Label>
                  <Form.Control type="email" value={auditorInfo.auditorEmail} disabled={!isEditingAuditor}
                    placeholder="auditor@example.com"
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, auditorEmail: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auditor Phone</Form.Label>
                  <Form.Control type="tel" value={auditorInfo.auditorPhone} disabled={!isEditingAuditor}
                    placeholder="+91-XXXXXXXXXX"
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, auditorPhone: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auditor Firm/Organization</Form.Label>
                  <Form.Control type="text" value={auditorInfo.auditorFirm} disabled={!isEditingAuditor}
                    placeholder="Firm Name"
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, auditorFirm: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control type="date" value={auditorInfo.appointmentDate} disabled={!isEditingAuditor}
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, appointmentDate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Certification Number</Form.Label>
                  <Form.Control type="text" value={auditorInfo.certificationNumber} disabled={!isEditingAuditor}
                    placeholder="CERT-XXXX"
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, certificationNumber: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Next Audit Date</Form.Label>
                  <Form.Control type="date" value={auditorInfo.nextAuditDate} disabled={!isEditingAuditor}
                    onChange={(e) => setAuditorInfo({ ...auditorInfo, nextAuditDate: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Periodic Measures */}
      <Card className="mb-4" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ background: T.white, borderBottom: '1px solid ' + T.border, padding: '16px 20px' }}>
          <h5 style={{ color: T.textPrimary, fontSize: '15px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaClipboardCheck /> Periodic Measures (as per {regCfg.legalBasis})
          </h5>
          {isEditingMeasures && (
            <Button variant="success" size="sm" onClick={handleUpdatePeriodicMeasures} style={{ background: T.success, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              <FaSave /> Save Changes
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <Alert variant="primary" className="mb-3">
            <strong>Compliance Requirements:</strong>
            <ul className="mb-0 compliancerequirements">
              <li>{regCfg.dpiaLabel} â€” {regCfg.dpiaLegalRef}</li>
              <li>{regCfg.auditLabel} â€” {regCfg.auditLegalRef}</li>
              <li>Any additional measures prescribed by {regCfg.regulatorName}</li>
            </ul>
          </Alert>

          <Card className="mb-3 border-primary" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center" style={{ background: SUSAN.primary + ' !important', padding: '14px 20px' }}>
              <span>
                {regCfg.dpiaLabel} Status
                <OverlayTrigger placement="right" overlay={dpiaInfoTooltip}>
                  <Button variant="link" size="sm" className="text-white p-0 ms-2">
                    <FaInfoCircle />
                  </Button>
                </OverlayTrigger>
              </span>
              <Badge bg="light" text="dark">
                Status: {periodicMeasures.dpiaStatus.toUpperCase()}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last {regCfg.dpiaLabel} Date</Form.Label>
                    <Form.Control type="date" value={periodicMeasures.lastDPIADate}
                      onChange={(e) => {
                        setPeriodicMeasures({ ...periodicMeasures, lastDPIADate: e.target.value });
                        setIsEditingMeasures(true);
                      }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Next {regCfg.dpiaLabel} Date (Due)</Form.Label>
                    <Form.Control type="date" value={periodicMeasures.nextDPIADate}
                      onChange={(e) => {
                        setPeriodicMeasures({ ...periodicMeasures, nextDPIADate: e.target.value });
                        setIsEditingMeasures(true);
                      }} />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col className="d-flex gap-2">
                  <Button variant="outline-primary" onClick={handleDownloadDPIATemplate} style={{ borderColor: SUSAN.primary, color: SUSAN.primary, borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                  
                    <FaDownload className="me-2" />Download Template
                  </Button>
                  <Form.Group>
                    <Form.Label className="btn btn-success mb-0" style={{ background: T.success, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                      <FaUpload className="me-2" />
                      {uploading ? "Uploading..." : "Upload DPIA"}
                      <Form.Control type="file" accept=".pdf,.doc,.docx"
                        onChange={handleDPIAUpload} disabled={uploading}
                        style={{ display: 'none' }} />
                    </Form.Label>
                  </Form.Group>
                  <Button variant="outline-info" onClick={handleDownloadUploadedDPIA}
                    disabled={!periodicMeasures.uploadedDPIA}
                    style={{ borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                    <FaFileDownload className="me-2" />Download Uploaded
                  </Button>
                </Col>
              </Row>

              {periodicMeasures.uploadedDPIA && (
                <Alert variant="success" className="mt-3 mb-0">
                  <FaCheckCircle className="me-2" />
                  {regCfg.dpiaLabel} uploaded successfully on {periodicMeasures.lastDPIADate}
                </Alert>
              )}
            </Card.Body>
          </Card>

          <Card className="border-secondary" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
            <Card.Header className="bg-secondary text-white" style={{ padding: '14px 20px' }}>
              {regCfg.auditLabel} â€” {regCfg.auditLegalRef}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last {regCfg.auditLabel} Date</Form.Label>
                    <Form.Control type="date" value={periodicMeasures.lastAuditDate}
                      onChange={(e) => {
                        setPeriodicMeasures({ ...periodicMeasures, lastAuditDate: e.target.value });
                        setIsEditingMeasures(true);
                      }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Next {regCfg.auditLabel} Date (Scheduled)</Form.Label>
                    <Form.Control type="date" value={periodicMeasures.nextAuditDate}
                      onChange={(e) => {
                        setPeriodicMeasures({ ...periodicMeasures, nextAuditDate: e.target.value });
                        setIsEditingMeasures(true);
                      }} />
                  </Form.Group>
                </Col>
              </Row>

              {auditorInfo.auditorEmail ? (
                <Button variant="outline-secondary" onClick={() => {
                  loadEmailTemplate("audit_request");
                  setEmailRecipient(auditorInfo.auditorEmail);
                  setShowEmailModal(true);
                }} style={{ borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
                  <FaEnvelope className="me-2" />Request {regCfg.auditLabel} from Auditor
                </Button>
              ) : (
                <Alert variant="warning" className="mb-0">
                  <FaInfoCircle className="me-2" />
                  Please add auditor email above to send audit requests
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Email Communication */}
      <Card className="mb-4" style={{ border: 'none', borderRadius: T.radiusLg, boxShadow: T.cardShadow, overflow: 'hidden' }}>
        <Card.Header style={{ background: T.white, borderBottom: '1px solid ' + T.border, padding: '16px 20px' }}>
          <h5 style={{ color: T.textPrimary, fontSize: '15px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaEnvelope /> DPO Email Communication
          </h5>
        </Card.Header>
        <Card.Body>
          <p>Send communications regarding {regCfg.emailTemplatePrefix} compliance, grievances, and data protection matters.</p>
          <div className="mb-3">
            <Button variant="outline-primary" className="me-2 mb-2"
              onClick={() => { loadEmailTemplate("grievance_acknowledgment"); setShowEmailModal(true); }}
              style={{ borderColor: SUSAN.primary, color: SUSAN.primary, borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              Grievance Acknowledgment
            </Button>
            <Button variant="outline-warning" className="me-2 mb-2"
              onClick={() => { loadEmailTemplate("breach_notification"); setShowEmailModal(true); }}
              style={{ borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              Breach Notification
            </Button>
            <Button variant="outline-info" className="me-2 mb-2"
              onClick={() => { loadEmailTemplate("compliance_reminder"); setShowEmailModal(true); }}
              style={{ borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              Compliance Reminder
            </Button>
            <Button variant="outline-success" className="mb-2"
              onClick={() => {
                loadEmailTemplate("audit_request");
                setEmailRecipient(auditorInfo.auditorEmail || "");
                setShowEmailModal(true);
              }}
              style={{ borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              Audit Request
            </Button>
          </div>
          <Button variant="primary" onClick={() => setShowEmailModal(true)} style={{ background: SUSAN.primary, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
            <FaEnvelope /> Compose Custom Email
          </Button>
        </Card.Body>
      </Card>

      {/* Email Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><FaEnvelope /> Compose Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSendEmail}>
            <Form.Group className="mb-3">
              <Form.Label>From (DPO Email)</Form.Label>
              <Form.Control type="email" value={dpoInfo.email} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>To (Recipient Email)</Form.Label>
              <Form.Control type="email" value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="recipient@example.com" required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Control type="text" value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Message Body</Form.Label>
              <Form.Control as="textarea" rows={8} value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)} required />
            </Form.Group>
            <Alert variant="warning">
              <small>All emails sent from this system are logged for compliance and audit purposes.</small>
            </Alert>
            <Button variant="primary" type="submit" style={{ background: SUSAN.primary, border: 'none', borderRadius: T.radiusSm, fontWeight: 600, fontSize: '13px' }}>
              <FaEnvelope /> Send Email
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DPOManagementTab;